import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { AccessTime, CheckCircleOutline, ErrorOutline, Refresh, SyncAlt } from "@mui/icons-material";
import { Alert, Box, Button, Card, CircularProgress, Stack, Typography } from "@mui/material";
import axiosInstance from "../AxiosInstance";
import EposTabs from "./EposTabs";

interface EposImportStatus {
    state: string;
    lastSuccessUtc: string | null;
    nextExtractionUtc: string | null;
}

const formatDateTime = (value: string | null, emptyLabel = "Not yet") => value
    ? new Intl.DateTimeFormat("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hourCycle: "h23"
    }).format(new Date(value))
    : emptyLabel;

const statePresentation = (state: string) => {
    switch (state) {
        case "running": return { label: "An import is running", icon: <SyncAlt fontSize="small" />, color: "primary.main" };
        case "due": return { label: "An import is due", icon: <AccessTime fontSize="small" />, color: "warning.main" };
        case "retryScheduled": return { label: "A retry is scheduled", icon: <AccessTime fontSize="small" />, color: "warning.main" };
        case "scheduled": return { label: "Imports are up to date", icon: <CheckCircleOutline fontSize="small" />, color: "success.main" };
        case "disabled": return { label: "Imports are paused", icon: <ErrorOutline fontSize="small" />, color: "text.secondary" };
        default: return { label: "Connection setup is needed", icon: <ErrorOutline fontSize="small" />, color: "text.secondary" };
    }
};

function SummaryRow({ label, value }: { label: string; value: string }) {
    return (
        <Stack component="li" direction="row" justifyContent="space-between" alignItems="baseline" spacing={2} sx={{ py: 1.5 }}>
            <Typography variant="body2" color="text.secondary">{label}</Typography>
            <Typography variant="body2" fontWeight={650} textAlign="right">{value}</Typography>
        </Stack>
    );
}

export default function EposImportStatusPage() {
    const [status, setStatus] = useState<EposImportStatus | null>(null);
    const [unmappedCount, setUnmappedCount] = useState(0);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState("");
    const statusRequestController = useRef<AbortController | null>(null);
    const hasLoadedStatus = useRef(false);

    const loadStatus = useCallback(async (signal?: AbortSignal, manual = false) => {
        if (manual) setRefreshing(true);
        setError("");
        try {
            const response = await axiosInstance.get<EposImportStatus>("/epos/import-status", { signal });
            if (!signal?.aborted) {
                setStatus(response.data);
                hasLoadedStatus.current = true;
            }
        } catch (requestError) {
            if (!signal?.aborted) {
                setError(axios.isAxiosError(requestError) && requestError.response?.status === 404
                    ? "EPOS import status is unavailable for this organisation."
                    : "Import status could not be loaded. Please try again.");
            }
        } finally {
            if (!signal?.aborted) {
                setLoading(false);
                setRefreshing(false);
            }
        }
    }, []);

    const refreshStatus = useCallback((manual = false) => {
        statusRequestController.current?.abort();
        const controller = new AbortController();
        statusRequestController.current = controller;
        if (!hasLoadedStatus.current) setLoading(true);
        void loadStatus(controller.signal, manual);
    }, [loadStatus]);

    useEffect(() => {
        const controller = new AbortController();
        refreshStatus();
        void axiosInstance.get<{ count: number }>("/epos/product-mappings/unmapped-count", { params: { retailer: 1 }, signal: controller.signal })
            .then(response => { if (!controller.signal.aborted) setUnmappedCount(response.data.count); })
            .catch(() => undefined);

        const intervalId = window.setInterval(() => refreshStatus(), 30_000);
        return () => {
            controller.abort();
            statusRequestController.current?.abort();
            window.clearInterval(intervalId);
        };
    }, [refreshStatus]);

    const presentation = statePresentation(status?.state ?? "notConfigured");
    const nextImport = !status || status.state === "notConfigured"
        ? "After setup"
        : status.state === "disabled"
            ? "When imports are enabled"
            : status.state === "running"
                ? "After the current import"
                : status.state === "due"
                    ? "Will begin shortly"
                    : formatDateTime(status.nextExtractionUtc, "Not scheduled");

    return (
        <Box sx={{ width: "100%", maxWidth: 1500, minWidth: 0, mx: "auto", py: { xs: 2, md: 3 }, px: { xs: 1, sm: 2 } }}>
            <EposTabs unmappedCount={unmappedCount} />

            {error && <Alert severity="error" action={<Button color="inherit" size="small" onClick={() => refreshStatus(true)}>Retry</Button>} sx={{ mb: 2 }}>{error}</Alert>}

            {loading ? (
                <Box sx={{ display: "grid", placeItems: "center", minHeight: 240 }}><CircularProgress /></Box>
            ) : status && (
                <Card variant="outlined" sx={{ p: { xs: 2, sm: 2.5 }, borderRadius: 3 }}>
                    <Stack direction={{ xs: "column", sm: "row" }} alignItems={{ xs: "stretch", sm: "center" }} justifyContent="space-between" spacing={1.5}>
                        <Stack direction="row" alignItems="center" spacing={1.25}>
                            <Box sx={{ display: "grid", placeItems: "center", width: 36, height: 36, flexShrink: 0, borderRadius: "50%", bgcolor: "action.hover", color: presentation.color }}>
                                {presentation.icon}
                            </Box>
                            <Typography variant="h6" sx={{ fontWeight: 750 }}>
                                {presentation.label}
                            </Typography>
                        </Stack>
                        <Button variant="outlined" startIcon={refreshing ? <CircularProgress size={16} /> : <Refresh />} onClick={() => refreshStatus(true)} disabled={refreshing} sx={{ alignSelf: { xs: "flex-start", sm: "auto" } }}>
                            Refresh
                        </Button>
                    </Stack>

                    {status.state === "disabled" && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            The EPOS connection is disabled in Integration settings. Enable it there to resume imports.
                        </Typography>
                    )}
                    {status.state === "notConfigured" && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            Add your EPOS connection details in Integration settings to start importing.
                        </Typography>
                    )}

                    <Box component="ul" aria-label="Import schedule and completion" sx={{ listStyle: "none", p: 0, m: 0, mt: 2, borderTop: 1, borderColor: "divider", "& > li + li": { borderTop: 1, borderColor: "divider" } }}>
                        <SummaryRow label="Next import" value={nextImport} />
                        <SummaryRow label="Last completed" value={formatDateTime(status.lastSuccessUtc)} />
                    </Box>
                </Card>
            )}
        </Box>
    );
}
